import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ThemeToggle } from "../ui/ThemeToggle";
import Button from "../ui/Button";
import { useAuth } from "../../context/AuthContext";
import FeedbackButton from "../feedback/FeedbackButton";

export type AdminNotification = {
  id: string;
  title: string;
  body: string;
  receivedAt: string;
  read: boolean;
  data?: Record<string, unknown>;
};

type AdminTopbarProps = {
  notifications?: AdminNotification[];
  unreadNotificationsCount?: number;
  onMarkNotificationAsRead?: (notificationId: string) => void;
  onMarkNotificationsAsRead?: () => void;
  onClearNotifications?: () => void;
  // Al hacer click en una notificación con data.reportId (Reporte
  // Operativo) — abre su detalle. El resto de las notificaciones no son
  // clickeables todavía (ver informe "Sprint Operativo", Parte 4).
  onNotificationClick?: (notification: AdminNotification) => void;
};

const wrapperStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  padding: "20px 24px 0 24px",
};

const barStyle: React.CSSProperties = {
  minHeight: "72px",
  border: "1px solid var(--border)",
  borderRadius: "22px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "14px 16px",
};

const leftStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  minWidth: 0,
};

const titleWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const eyebrowStyle: React.CSSProperties = {
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--text-secondary)",
};

const titleStyle: React.CSSProperties = {
  fontSize: "20px",
  fontWeight: 800,
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const rightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexShrink: 0,
};

const profileChipStyle: React.CSSProperties = {
  minHeight: "46px",
  padding: "0 14px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "flex",
  alignItems: "center",
  fontSize: "14px",
  fontWeight: 600,
  maxWidth: "240px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const bellWrapStyle: React.CSSProperties = {
  position: "relative",
};

const bellButtonStyle: React.CSSProperties = {
  position: "relative",
  width: "46px",
  height: "46px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "18px",
};

const badgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "-6px",
  right: "-6px",
  minWidth: "20px",
  height: "20px",
  padding: "0 6px",
  borderRadius: "999px",
  background: "var(--primary)",
  color: "#FFFFFF",
  border: "2px solid var(--surface)",
  fontSize: "11px",
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const panelStyle: React.CSSProperties = {
  position: "absolute",
  top: "64px",
  right: 0,
  width: "360px",
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "20px",
  border: "1px solid var(--border)",
  background: "var(--background)",
  color: "var(--text-primary)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  overflow: "hidden",
  zIndex: 50,
};

const panelHeaderStyle: React.CSSProperties = {
  padding: "16px",
  borderBottom: "1px solid var(--border)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "15px",
  fontWeight: 800,
};

const panelActionStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer",
};

const itemActionStyle: React.CSSProperties = {
  marginTop: "10px",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-primary)",
  borderRadius: "999px",
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 800,
  cursor: "pointer",
};

const listStyle: React.CSSProperties = {
  maxHeight: "380px",
  overflowY: "auto",
};

const itemStyle: React.CSSProperties = {
  padding: "14px 16px",
  borderBottom: "1px solid var(--border)",
};

const itemUnreadStyle: React.CSSProperties = {
  background: "var(--primary-soft)",
  borderLeft: "3px solid var(--primary)",
};

const itemTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  marginBottom: "6px",
};

const itemBodyStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  lineHeight: 1.45,
};

const itemDateStyle: React.CSSProperties = {
  marginTop: "8px",
  fontSize: "11px",
  color: "var(--text-secondary)",
};

const emptyStyle: React.CSSProperties = {
  padding: "24px 16px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  textAlign: "center",
};

const getPageTitle = (pathname: string): string => {
  if (pathname === "/admin") return "Dashboard";
  if (pathname.includes("/torre-control")) return "Torre de control";

  if (pathname.includes("/operacion")) return "Operación del día";
  if (pathname.includes("/envios/carga")) return "Carga de envíos";
  if (pathname.includes("/envios/etiquetas")) return "Impresión de etiquetas";
  if (pathname.includes("/envios")) return "Lista de envíos";
  if (pathname.includes("/recorridos/fijas")) return "Colectas fijas";
  if (pathname.includes("/recorridos")) return "Recorridos";

  if (pathname.includes("/deposito/planta")) return "Planta";
  if (pathname.includes("/deposito/estanterias")) return "Estanterías";
  if (pathname.includes("/deposito/baul")) return "Baúl";
  if (pathname.includes("/choferes/mapa")) return "Choferes en vivo";

  if (pathname.includes("/liquidaciones/carga-manual")) return "Carga manual";
  if (pathname.includes("/liquidaciones/totales")) return "Totales";
  if (pathname.includes("/stores")) return "Tiendas";
  if (pathname.includes("/users")) return "Usuarios";
  if (pathname.includes("/listas-de-precios")) return "Listas de precios";
  if (pathname.includes("/sistema/sugerencias")) return "Sugerencias y errores";

  return "Panel administrativo";
};

const formatNotificationDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const AdminTopbar = ({
  notifications = [],
  unreadNotificationsCount = 0,
  onMarkNotificationAsRead,
  onMarkNotificationsAsRead,
  onClearNotifications,
  onNotificationClick,
}: AdminTopbarProps) => {
  const location = useLocation();
  const { firstName, displayName, logout } = useAuth();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const pageTitle = getPageTitle(location.pathname);
  const profileLabel = firstName || displayName || "Administrador";

  const visibleNotifications = useMemo(
    () => notifications.slice(0, 50),
    [notifications],
  );

  const handleToggleNotifications = () => {
    setNotificationsOpen((prev) => {
      const nextOpen = !prev;

      // if (nextOpen) {
      //   onMarkNotificationsAsRead?.();
      // }

      const handleToggleNotifications = () => {
  setNotificationsOpen((prev) => !prev);
};

      return nextOpen;
    });
  };

  const handleMarkSingleAsRead = (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationId: string,
  ) => {
    event.stopPropagation();
    onMarkNotificationAsRead?.(notificationId);
  };

  const handleItemClick = (item: AdminNotification) => {
    if (!item.data?.reportId) return;
    onMarkNotificationAsRead?.(item.id);
    onNotificationClick?.(item);
    setNotificationsOpen(false);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div style={wrapperStyle}>
      <div style={barStyle}>
        <div style={leftStyle}>
          <div style={titleWrapStyle}>
            <span style={eyebrowStyle}>Lynx Platform</span>
            <span style={titleStyle}>{pageTitle}</span>
          </div>
        </div>

        <div style={rightStyle}>
          <FeedbackButton />

          <div style={bellWrapStyle}>
            <button
              type="button"
              style={bellButtonStyle}
              onClick={handleToggleNotifications}
              title="Notificaciones"
              aria-label="Notificaciones"
            >
              🔔

              {unreadNotificationsCount > 0 ? (
                <span style={badgeStyle}>
                  {unreadNotificationsCount > 9 ? "9+" : unreadNotificationsCount}
                </span>
              ) : null}
            </button>

            {notificationsOpen ? (
              <div style={panelStyle}>
                <div style={panelHeaderStyle}>
                  <h3 style={panelTitleStyle}>Notificaciones</h3>

                  {visibleNotifications.length ? (
                    <button
                      type="button"
                      style={panelActionStyle}
                      onClick={onClearNotifications}
                    >
                      Marcar todas
                    </button>
                  ) : null}
                </div>

                <div style={listStyle}>
                  {visibleNotifications.length ? (
                    visibleNotifications.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        style={{
                          ...itemStyle,
                          ...(!item.read ? itemUnreadStyle : {}),
                          ...(item.data?.reportId ? { cursor: "pointer" } : {}),
                        }}
                      >
                        <div style={itemTitleStyle}>{item.title}</div>
                        <div style={itemBodyStyle}>{item.body}</div>
                        <div style={itemDateStyle}>
                          {formatNotificationDate(item.receivedAt)}
                        </div>

                        {!item.read ? (
                          <button
                            type="button"
                            style={itemActionStyle}
                            onClick={(event) =>
                              handleMarkSingleAsRead(event, item.id)
                            }
                          >
                            Marcar leída
                          </button>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div style={emptyStyle}>Todavía no hay notificaciones.</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <ThemeToggle />

          <div style={profileChipStyle} title={`Perfil · ${profileLabel}`}>
            {`Perfil · ${profileLabel}`}
          </div>

          <Button variant="ghost" onClick={handleLogout}>
            Salir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminTopbar;